import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { capitalize } from '../../utils/capitalize';
import { Button } from '../button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';

interface CalendarCaptionProps {
  baseId?: string;
  displayMonth: Date;
  yearsRange?: number;
  onChangeMonth: (date: Date) => void;
}

export function CalendarCaption({
  baseId,
  displayMonth,
  yearsRange = 10,
  onChangeMonth,
}: CalendarCaptionProps) {
  const currentMonth = displayMonth.getMonth();
  const currentYear = displayMonth.getFullYear();

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i,
    label: capitalize(format(new Date(currentYear, i, 1), 'MMMM', { locale: es })),
  }));

  const evenYearsRange = yearsRange % 2 !== 0 ? yearsRange - 1 : yearsRange;
  const years = Array.from(
    { length: evenYearsRange + 1 },
    (_, i) => currentYear - evenYearsRange / 2 + i,
  );

  const goToPrevMonth = () => {
    const newDate = new Date(currentYear, currentMonth - 1, 1);
    onChangeMonth(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentYear, currentMonth + 1, 1);
    onChangeMonth(newDate);
  };

  return (
    <div className="flex w-full items-center justify-center gap-3">
      <Button
        id={baseId ? `${baseId}-prev-month` : undefined}
        size="icon"
        variant="secondary"
        onClick={goToPrevMonth}
      >
        <ChevronLeftIcon className="size-4" />
      </Button>

      <Select
        value={String(currentMonth)}
        onValueChange={(value) => {
          const newDate = new Date(currentYear, Number(value), 1);
          onChangeMonth(newDate);
        }}
      >
        <SelectTrigger
          id={baseId ? `${baseId}-select-month` : undefined}
          className="w-[8rem] text-sm"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="h-[15rem] w-[8rem]">
          {months.map((month) => (
            <SelectItem
              id={baseId ? `${baseId}-select-month-${month.value}` : undefined}
              className="text-sm"
              key={month.value}
              value={String(month.value)}
            >
              {month.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={String(currentYear)}
        onValueChange={(value) => {
          const newDate = new Date(Number(value), currentMonth, 1);
          onChangeMonth(newDate);
        }}
      >
        <SelectTrigger
          id={baseId ? `${baseId}-select-year` : undefined}
          className="w-[5.5rem] text-sm"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-[15rem] w-[5.5rem] min-w-[5.5rem]">
          {years.map((year) => (
            <SelectItem
              id={baseId ? `${baseId}-select-year-${year}` : undefined}
              className="text-sm"
              key={year}
              value={String(year)}
            >
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        id={baseId ? `${baseId}-next-month` : undefined}
        size="icon"
        variant="secondary"
        onClick={goToNextMonth}
      >
        <ChevronRightIcon className="size-4" />
      </Button>
    </div>
  );
}
